/* eslint-disable @typescript-eslint/no-var-requires */
import { csvImport } from "../../utils/csv-string-to-ob";
import raw from "raw.macro";
import ShowLess from "../../partials/ShowLess";

const styles = require("./data-annotation.module.scss");
const attributeData = csvImport(raw("./attribute-data.csv"));
const categoryData = csvImport(raw("./category-data.csv"));

const categoryFields = Object.keys(categoryData[0]);
const attributeFields = Object.keys(attributeData[0]);

const CategoryRow = ({
  row,
  isLast = false,
  fields = [],
}: {
  row: Record<string, string>;
  isLast: boolean;
  fields: string[];
}) => {
  const isTotal = row[fields[0]] === "Total";

  const value = (key) => {
    return isTotal ? <strong>{row[key]}</strong> : row[key];
  };

  const tableRow = (
    <tr>
      {fields.map((f) => (
        <td key={f}>{value(f)}</td>
      ))}
    </tr>
  );

  if (isTotal && !isLast) {
    return (
      <>
        {tableRow}
        <tr>
          <td />
          <td />
          <td />
          <td />
          <td />
        </tr>
      </>
    );
  }

  return tableRow;
};

export default () => (
  <ShowLess className={styles.wrapper + " text-justify pb-2em"}>
    <div className="content">
      <p>
        After collecting the driving data, we sample well synchronized{" "}
        <i>keyframes</i> (image, LIDAR, RADAR) at 2Hz and send them to our
        annotation partner{" "}
        <a href="https://scaleapi.com/" target="_blank" rel="noreferrer">
          Scale
        </a>{" "}
        for annotation. Using expert annotators and multiple validation steps,
        we achieve highly accurate annotations. All objects in the nuScenes
        dataset come with a semantic category, as well as a a 3D bounding box
        and attributes for each frame they occur in. Compared to 2D bounding
        boxes, this allows us to accurately infer an object’s position and
        orientation in space.
      </p>
      <p>
        We provide ground truth labels for 23 object classes. For a detailed
        definition of every class and example images, please see the{" "}
        <a
          href="https://github.com/nutonomy/nuscenes-devkit/blob/master/docs/instructions_nuscenes.md"
          target="_blank"
          rel="noreferrer"
        >
          annotator instructions
        </a>
        . For the full nuScenes dataset we provide annotations for the following
        categories (excl. test set):
      </p>
      <p>
        For nuScenes-lidarseg, we annotate every point in the lidar pointcloud
        with a semantic label. In addition to the 23 foreground classes (things)
        from nuScenes, we have included 9 background classes (stuff). For a
        detailed definition of every class and example images, please see the
        annotator instructions for{" "}
        <a
          href="https://github.com/nutonomy/nuscenes-devkit/blob/master/docs/instructions_nuscenes.md"
          target="_blank"
          rel="noreferrer"
        >
          nuScenes
        </a>{" "}
        and{" "}
        <a
          href="https://github.com/nutonomy/nuscenes-devkit/blob/master/docs/instructions_lidarseg.md"
          target="_blank"
          rel="noreferrer"
        >
          nuScenes-lidarseg
        </a>
        . We provide annotations for the following categories (excl. test set):
      </p>
      <div>
        <table className={styles.halfWidth}>
          <thead>
            <tr>
              {categoryFields.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categoryData.map((row, index) => (
              <CategoryRow
                row={row}
                key={index}
                isLast={index === categoryData.length - 1}
                fields={categoryFields}
              />
            ))}
          </tbody>
        </table>
        <p>
          * Note that the <i>static_object.bicycle_rack category</i> can include
          bicycles that are not annotated individually. We use it to ignore
          large groups of shared bicycles during training to avoid biasing our
          object detector towards these less interesting bicycles.
        </p>
        <p>
          Furthermore certain classes <b>in nuScenes</b> have special
          attributes:
        </p>
        <table className={styles.halfWidth}>
          <thead>
            <tr>
              {attributeFields.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {attributeData.map((row, index) => (
              <CategoryRow
                row={row}
                key={index}
                isLast={index === attributeData.length - 1}
                fields={attributeFields}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </ShowLess>
);
